// @ts-nocheck
// app/faq.tsx — Editorial FAQ page with FAQPage Schema preserved

import { useEffect, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import Head from 'expo-router/head';
import { useRouter } from 'expo-router';

import { theme, ACCENT, INK, INK_MUTED, INK_SOFT, PAPER, PAPER_SOFT } from '@/constants/theme';
import {
  DoubleRule, EditorialFooter, Eyebrow, Headline, Numeral, Paper, PageHero, Section,
} from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';
import { AccessibilityButton } from '@/components/AccessibilityButton';

const SITE_URL = 'https://kursiaii.com';
const SERIF = theme.fonts.serif;
const SERIF_BOLD = theme.fonts.serifBold;
const SERIF_BLACK = theme.fonts.serifBlack;
const SANS = theme.fonts.regular;
const MONO = theme.fonts.mono;
const IS_WEB = Platform.OS === 'web';

const FAQ_ITEMS = [
  { q: 'מה זה Kursi?', a: 'Kursi היא פלטפורמה ישראלית לחיפוש קורסים המופעלת בבינה מלאכותית. הפלטפורמה מאגדת מעל 1,200 קורסים מכל התחומים — תכנות, שיווק דיגיטלי, AI, פסיכומטרי, שוק ההון, סייבר ועוד — ומסייעת לכל אחד למצוא את הקורס שהכי מתאים לו בהתאם לצרכים, לרמה ולתקציב שלו.' },
  { q: 'איך Kursi עוזר לי למצוא קורס?', a: 'אתם כותבים לנו בשורה אחת מה אתם רוצים ללמוד — ה-AI שלנו מנתח את הבקשה ומחזיר לכם 3 התאמות מדויקות מתוך הקטלוג. ככל שתפרטו יותר (רמה, פורמט, תקציב), כך ההמלצות יהיו מדויקות יותר.' },
  { q: 'כמה קורסים יש בקטלוג של Kursi?', a: 'הקטלוג של Kursi כולל מעל 1,200 קורסים מקורסים אונליין ועד קורסים פרונטליים, מסמינרים חד-יומיים ועד תכניות לימוד ממושכות. הקטלוג מתעדכן באופן שוטף.' },
  { q: 'האם השירות של Kursi חינמי?', a: 'כן — חיפוש הקורסים, הצ׳אט החכם וצפייה בקטלוג הם חינמיים לחלוטין. Kursi לא גובה דמי מנוי או עמלות מהמשתמשים. במקרים מסוימים ניתן לקבל הטבות בלעדיות על קורסים דרך הפלטפורמה.' },
  { q: 'אילו תחומים מכוסים בקטלוג?', a: 'הקטלוג מכסה עשרות תחומים: תכנות, שיווק דיגיטלי, AI, דאטה, סייבר, UI/UX, שוק ההון, ניהול מוצר, מכירות, פסיכומטרי, פיננסים, DevOps, אנגלית מקצועית ועוד.' },
  { q: 'מה ההבדל בין קורס אונליין לקורס פרונטלי?', a: 'קורס אונליין מתקיים מרחוק בגמישות. קורס פרונטלי מתקיים פנים אל פנים. ב-Kursi תוכלו לסנן לפי פורמט ולמצוא גם קורסים היברידיים.' },
  { q: 'האם Kursi ממליצה על מכללות ספציפיות?', a: 'Kursi לא ממליצה על מכללה ספציפית — אנחנו מציגים נתונים אובייקטיביים על כל קורס. ההחלטה תמיד נשארת אצלכם.' },
  { q: 'כמה זמן לוקח למצוא קורס מתאים?', a: 'רוב המשתמשים מקבלים 3 המלצות איכותיות תוך 30–60 שניות. ככל שתוסיפו פרטים — ההתאמה מדויקת יותר.' },
  { q: 'האם אפשר לסנן לפי מחיר?', a: 'כן. בקטלוג יש מסנן מחיר — מקורסים חינמיים ועד תכניות פרמיום. חלק מהקורסים כוללים קופון הנחה בלעדי דרך Kursi.' },
  { q: 'איך פונים אליכם בשאלות נוספות?', a: 'דרך אינסטגרם @kursi_il או דרך המייל hello@kursiaii.com. נחזור אליכם תוך 48 שעות.' },
  { q: 'האם Kursi מתאים לסטודנטים שמחפשים מכינות?', a: 'בהחלט. אנחנו מכסים פסיכומטרי, מכינות אקדמיות ומסלולי הכנה לתואר.' },
  { q: 'איך אני יודע שהמידע עדכני?', a: 'הקטלוג מתעדכן באופן שוטף. עם זאת, מומלץ לאמת מחיר ומועדי פתיחה ישירות מול הספק לפני הרישום.' },
  { q: 'האם Kursi מתאים גם לעסקים?', a: 'כן. ניתן לסנן לפי תחום ורמה כדי למצוא הכשרה מתאימה לצוות שלכם.' },
];

export default function FaqPage() {
  const router = useRouter();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  useEffect(() => {
    if (!IS_WEB || typeof document === 'undefined') return;
    const inject = (id: string, data: object) => {
      const ex = document.getElementById(id);
      if (ex) ex.remove();
      const s = document.createElement('script');
      s.type = 'application/ld+json'; s.id = id; s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
    };
    inject('faq-jsonld', {
      '@context': 'https://schema.org', '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map(i => ({
        '@type': 'Question', name: i.q,
        acceptedAnswer: { '@type': 'Answer', text: i.a },
      })),
    });
    inject('breadcrumb-jsonld', {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'דף הבית', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'שאלות נפוצות', item: `${SITE_URL}/faq` },
      ],
    });
    return () => {
      document.getElementById('faq-jsonld')?.remove();
      document.getElementById('breadcrumb-jsonld')?.remove();
    };
  }, []);

  return (
    <Paper>
      <AccessibilityButton />
      {IS_WEB && (
        <Head>
          <title>שאלות נפוצות · Kursi</title>
          <meta name="description" content="כל מה שרציתם לדעת על Kursi — מי אנחנו, איך עובד החיפוש, ומה ההבטחה שלנו לכם." />
          <link rel="canonical" href="https://kursiaii.com/faq" />
        </Head>
      )}

      <ScrollView style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER }}>
        <EditorialHeader />

        <Section py="xl" px="md" max={1000}>
          <PageHero
            kicker="גיליון השאלות · עיתון Kursi"
            title={<>שלוש עשרה שאלות,{'\n'}<Text style={{ color: ACCENT, fontStyle: 'italic' as any }}>שלוש עשרה תשובות.</Text></>}
            lede="הכל מה שאתם תוהים על Kursi — איך אנחנו עובדים, איפה אנחנו עוצרים, ומה ההבטחה שלנו. בעברית רגילה, בלי משפטים שיווקיים."
            meta={`${FAQ_ITEMS.length} שאלות · עודכן 2025`}
          />
        </Section>

        <Section py="lg" px="md" max={980}>
          <View style={{ borderTopWidth: 2, borderTopColor: INK }}>
            {FAQ_ITEMS.map((item, i) => {
              const open = openIndex === i;
              return (
                <Pressable
                  key={i}
                  onPress={() => setOpenIndex(open ? null : i)}
                  style={s.row}
                  accessibilityRole="button"
                >
                  <View style={{ flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 18 } as any}>
                    <Numeral size={48} color={open ? ACCENT : INK}>{String(i + 1).padStart(2, '0')}</Numeral>
                    <View style={{ flex: 1 }}>
                      <View style={{ flexDirection: 'row-reverse', justifyContent: 'space-between', alignItems: 'flex-start', gap: 18 } as any}>
                        <Text style={s.q}>{item.q}</Text>
                        <Text style={s.toggle}>{open ? '–' : '+'}</Text>
                      </View>
                      {open && (
                        <View style={{ marginTop: 16 }}>
                          <Text style={s.a}>{item.a}</Text>
                        </View>
                      )}
                    </View>
                  </View>
                </Pressable>
              );
            })}
          </View>
        </Section>

        <Section py="xxl" px="md" max={980}>
          <View style={s.endNote}>
            <Eyebrow color={ACCENT}>לא מצאתם תשובה?</Eyebrow>
            <View style={{ height: 12 }} />
            <Headline level={2} color={PAPER}>תכתבו לנו. אנחנו עונים בכבוד.</Headline>
            <View style={{ height: 14 }} />
            <Text style={s.endNoteBody}>
              hello@kursiaii.com · אינסטגרם @kursi_il
            </Text>
          </View>
        </Section>

        <EditorialFooter
          columns={[
            { title: 'מקטעים', items: [
              { label: 'הקטלוג המלא', href: '/catalog' },
              { label: 'תחומי לימוד', href: '/fields' },
              { label: 'שאלון התאמה', href: '/quiz' },
            ]},
            { title: 'המוצר', items: [
              { label: 'איך זה עובד', href: '/how-it-works' },
              { label: 'המתודולוגיה', href: '/methodology' },
              { label: 'אודות', href: '/about' },
            ]},
          ]}
          onNavigate={(href) => router.push(href as any)}
        />
      </ScrollView>
    </Paper>
  );
}

const s = StyleSheet.create({
  row: { paddingVertical: 24, borderBottomWidth: 1, borderBottomColor: INK },
  q: { flex: 1, fontFamily: SERIF_BOLD, fontSize: 22, color: INK, letterSpacing: -0.3, textAlign: 'right', lineHeight: 30 },
  a: { fontFamily: SANS, fontSize: 16, lineHeight: 27, color: INK_SOFT, textAlign: 'right' },
  toggle: { fontFamily: SERIF_BLACK, fontSize: 28, color: INK, width: 24, textAlign: 'center', lineHeight: 28 },
  endNote: { backgroundColor: INK, padding: 36 },
  endNoteBody: { fontFamily: MONO, fontSize: 14, color: PAPER_SOFT, letterSpacing: 1.2, textAlign: 'right' },
});
